// 결과 테이블
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "../IntegratedSearchPage.module.css";
import { Icon } from "./Icons";
import { labelFor } from "../data/searchData";

/* 한 화면에 보여줄 행 수 (자동 맞춤 대신 사용자가 고른다) */
export const PAGE_SIZE_OPTIONS = [10, 20, 50];

export function ResultsTable({
    rows, selectedKeys, toggleOne, dataAsOf, dataStale, mutIncludeGenes,
    focusedKey, setFocusedKey, sort, toggleSort,
    page, totalPages, pageSize, filteredCount, setPage, setPageSize, selectedDrug, setSelectedDrug, drugOptions,
    allFilteredSelected, selectAllFiltered, clearSelection, selectedCount,
}) {
    const { t } = useTranslation();
    const scrollRef = useRef(null);
    // Mutation 컬럼은 "필터로 고른 유전자"만 보여준다(2026-09-17 연구원 요청).
    // 아무것도 안 골랐으면 값을 나열하지 않고 안 골랐다고 적는다.
    const pickSet = useMemo(() => new Set(mutIncludeGenes || []), [mutIncludeGenes]);

    /* 헤더 체크박스 = "필터된 전체" 선택/해제.
       페이지 단위 선택을 두지 않으므로 안내 배너가 필요 없고,
       따라서 테이블 높이가 변하지 않아 pageSize 재계산·행 밀림이 발생하지 않는다. */
    const [selecting, setSelecting] = useState(false);
    const someSelected = !allFilteredSelected && selectedCount > 0;
    const toggleAll = async () => {
        if (selecting) return;
        if (allFilteredSelected) { clearSelection(); return; }
        setSelecting(true);
        try { await selectAllFiltered(); } finally { setSelecting(false); }
    };

    // 페이지 직접 입력: 입력 중에는 로컬값만 갱신, 엔터/포커스아웃 시 실제 이동
    const [pageInput, setPageInput] = useState(String(page));
    useEffect(() => { setPageInput(String(page)); }, [page]);  // 외부에서 page 바뀌면 동기화
    const commitPage = () => {
        const v = parseInt(pageInput, 10);
        if (isNaN(v)) { setPageInput(String(page)); return; }   // 잘못된 입력 → 원래값 복원
        const clamped = Math.min(Math.max(1, v), totalPages || 1);
        setPage(clamped);
        setPageInput(String(clamped));
    };

    // 창 높이에 맞춰 pageSize 를 자동 계산하던 로직은 제거했다(2026-09-17).
    // 사용자가 10/20/50 중에서 고르고, 넘치는 행은 스크롤로 본다.
    // (자동 계산을 남겨두면 창 크기가 바뀔 때 사용자의 선택을 덮어쓴다)

    // 오가노이드(gbccKey) 병합: 연속 동일 gbccKey 의 첫 행에서만 rowSpan
    const rowMeta = rows.map((r, i) => {
        const prev = rows[i - 1];
        const firstOfGroup = !prev || prev.gbccKey !== r.gbccKey;
        let span = 1;
        if (firstOfGroup) {
            for (let j = i + 1; j < rows.length && rows[j].gbccKey === r.gbccKey; j++) span++;
        }
        return { firstOfGroup, span };
    });

    const cols = [
        { k: "id", label: "Organoid (GBCC)", width: "20%" },
        { k: "ngs", label: "NGS (배지그룹)", width: "22%" },
        { k: "organ", label: "장기", width: "12%" },
        { k: "subtype", label: "Subtype", width: "16%" },
        { k: "mutations", label: "Mutation", width: "18%" },
        { k: "ic50", label: "IC50", width: "10%", num: true },
        { k: "fusions", label: "Fusion", width: "6%", num: true },
    ];

    const sortLabel = t(cols.find((c) => c.k === sort.key)?.label || sort.key);

    return (
        <div className={styles.tableWrap}>
            <div className={styles.tableToolbar}>
                <div className={styles.tableToolbarLeft}>
                    <span className={styles.tableTitle}>{t('검색 결과')}</span>
                    <span className={styles.tableSort}>{t('정렬')}: {sortLabel} {sort.dir === "asc" ? "↑" : "↓"}</span>
                </div>
                <div className={styles.tableToolbarRight}>
                    <DataAsOf ts={dataAsOf} stale={dataStale} />
                    {selectedCount > 0 && <span className={styles.selCount}>{selectedCount}{t('개 선택')}</span>}
                </div>
            </div>

            <div className={styles.tableArea}>
                <div className={styles.tableScroll} ref={scrollRef}>
                <table className={styles.table}>
                    <colgroup>
                        <col style={{ width: 48 }} />
                        {cols.map((c) => <col key={c.k} style={{ width: c.width }} />)}
                    </colgroup>
                    <thead>
                        <tr className={styles.headRow}>
                            <th className={styles.thFirst}>
                                <input type="checkbox"
                                    checked={allFilteredSelected}
                                    disabled={selecting}
                                    ref={(el) => { if (el) el.indeterminate = someSelected; }}
                                    onChange={toggleAll}
                                    className={styles.checkbox}
                                    title={allFilteredSelected
                                        ? t('전체 선택 해제')
                                        : `${t('검색된 전체 선택')} (${filteredCount})`} />
                            </th>
                            {cols.map((c) => (
                                <th key={c.k} className={c.num ? styles.thCellNum : styles.thCell}>
                                    <div className={c.k === "ic50" ? styles.thStack : undefined}>
                                        <button className={c.num ? `${styles.thBtn} ${styles.thBtnNum}` : styles.thBtn} onClick={() => toggleSort(c.k)}
                                            title={t('정렬: 오름차순 → 내림차순 → 해제')}>
                                            {t(c.label)}
                                            <span className={styles.thSort} style={{ opacity: sort.key === c.k ? 1 : 0.4 }}>
                                                {sort.key === c.k
                                                    ? (sort.dir === "asc" ? <Icon.SortUp /> : <Icon.SortDown />)
                                                    : <Icon.Sort />}
                                            </span>
                                        </button>
                                        {c.k === "ic50" && (
                                            <select className={styles.ic50DrugSelect}
                                                value={selectedDrug || ""}
                                                onClick={(e) => e.stopPropagation()}
                                                onMouseDown={(e) => e.stopPropagation()}
                                                onChange={(e) => setSelectedDrug(e.target.value || null)}
                                                title={t('IC50 표시 약물 선택')}>
                                                {/* 선택 없음이 기본값. 예전에는 목록 첫 약물이 자동 선택돼
                                                    고르지도 않은 약물의 IC50 이 떠 있었다(2026-09-17 수정) */}
                                                <option value="">NA</option>
                                                {(drugOptions || []).map((d) => (
                                                    <option key={d} value={d}>{d}</option>
                                                ))}
                                            </select>
                                        )}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, ri) => {
                            const isFocused = focusedKey === r.groupKey;
                            const isSelected = selectedKeys.has(r.groupKey);
                            const rowClass = [styles.row, isFocused && styles.rowFocused, isSelected && styles.rowSelected].filter(Boolean).join(" ");
                            const meta = rowMeta[ri];
                            const ic50 = selectedDrug ? (r.drugIC50 ? r.drugIC50[selectedDrug] : undefined) : undefined;
                            return (
                                <tr key={r.groupKey} className={rowClass} onClick={() => setFocusedKey(r.groupKey)}>
                                    <td className={styles.tdFirst} onClick={(e) => e.stopPropagation()}>
                                        <input type="checkbox" checked={isSelected} onChange={() => toggleOne(r.groupKey)} className={styles.checkbox} />
                                    </td>
                                    {meta.firstOfGroup && (
                                        <td className={styles.tdOrganoid} rowSpan={meta.span}>
                                            <span className={styles.idMain}>{r.id}</span>
                                        </td>
                                    )}
                                    <td className={styles.tdCell}><NgsBadges r={r} /></td>
                                    <td className={styles.tdCell}>
                                        <div className={styles.organStack}>
                                            <span className={styles.organName}>{r.organ}</span>
                                            <span className={r.dx === "Primary" ? styles.dxPrimary : styles.dxMetastatic}>
                                                <span className={styles.dot} /> {t(labelFor(r.dx))}
                                            </span>
                                        </div>
                                    </td>
                                    <td className={styles.tdCell}><span className={styles.subtypeText}>{r.subtype}</span></td>
                                    <td className={styles.tdCell}>
                                        <div className={styles.mutationRow}>
                                            {(() => {
                                                // 필터에서 고른 유전자만 보여준다.
                                                // 아무것도 안 골랐으면 값 대신 "안 골랐다"고 알린다.
                                                if (pickSet.size === 0) {
                                                    return <span className={styles.mutNoFilter}>{t('Mutation 필터 미선택')}</span>;
                                                }
                                                const hit = (r.mutations || []).filter((m) => pickSet.has(m));
                                                if (hit.length === 0) return <span className={styles.ic50None}>—</span>;
                                                return hit.map((m) => (
                                                    <span key={m} className={styles.mutToken}>{m}</span>
                                                ));
                                            })()}
                                        </div>
                                    </td>
                                    <td className={styles.tdCellNum}><IC50Cell value={ic50} hasDrug={!!selectedDrug} /></td>
                                    <td className={styles.tdCellNum}><FusionPill value={r.fusions} hasRNA={r.hasRNA} /></td>
                                </tr>
                            );
                        })}
                        {rows.length === 0 && (
                            <tr><td colSpan={cols.length + 1} className={styles.tableEmpty}>{t('현재 필터에 맞는 샘플이 없습니다.')}</td></tr>
                        )}
                    </tbody>
                </table>
                </div>
            </div>

            <div className={styles.tableFooter}>
                <span className={styles.mono11}>
                    {filteredCount === 0 ? 0 : (page - 1) * pageSize + 1}–{Math.min(page * pageSize, filteredCount)} / {filteredCount}
                </span>
                <div className={styles.pager}>
                    <span className={styles.pageSizeWrap}>
                        <select className={styles.pageSizeSelect}
                            value={pageSize}
                            onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(1); }}
                            title={t('한 페이지에 보여줄 개수')}>
                            {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                        <span className={styles.mono11}>{t('개씩')}</span>
                    </span>
                    <button className={styles.miniBtn} disabled={page <= 1} onClick={() => setPage(1)} title={t('처음')}>«</button>
                    <button className={styles.miniBtn} disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} title={t('이전')}>‹</button>
                    <input className={styles.pageInput} type="number" min={1} max={totalPages || 1}
                        value={pageInput}
                        onChange={(e) => setPageInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.target.blur(); } }}
                        onBlur={commitPage}
                        title={t('페이지 번호 입력 후 Enter')} />
                    <span className={styles.mono11}>/ {totalPages}</span>
                    <button className={styles.miniBtn} disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} title={t('다음')}>›</button>
                    <button className={styles.miniBtn} disabled={page >= totalPages} onClick={() => setPage(totalPages)} title={t('끝')}>»</button>
                </div>
            </div>
        </div>
    );
}

/* 데이터 기준 시각.
 * 목록은 백엔드 메모리 캐시(기본 30분)에서 나오므로 "지금 DB 상태"와 다를 수 있다.
 * stale=true 면 이 응답은 낡은 캐시이고 서버가 뒤에서 재빌드 중 → 잠시 후 새로고침하면 최신이 된다. */
export function DataAsOf({ ts, stale }) {
    const { t } = useTranslation();
    if (!ts) return null;
    const d = new Date(ts);
    if (isNaN(d.getTime())) return null;
    const p = (n) => String(n).padStart(2, "0");
    const label = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
    return (
        <span className={styles.tableSort}
            title={stale
                ? t('새 데이터를 준비 중입니다. 잠시 후 새로고침하면 최신 데이터가 표시됩니다.')
                : t('목록 데이터가 서버에서 집계된 시각입니다.')}>
            {t('데이터 기준')} {label}{stale ? ` · ${t('갱신 중…')}` : ""}
        </span>
    );
}

export function NgsBadges({ r }) {
    // 한 배지그룹에 NGS 샘플이 여러 건일 수 있다(계대 차이, 약물처리 검체 등).
    // 대표 코드 1개만 적으면 옆 컬럼의 Mutation/CNV(= 전체 합집합)와 어긋나 보이므로
    // "대표코드 +N" 으로 나머지 건수를 함께 표기하고, 전체 목록은 툴팁으로 확인한다.
    const list = r.ngsList || [];
    const primary = r.hasWES ? r.ngsWES : r.ngsRNA;
    const extra = Math.max(0, list.length - 1);

    let tip = "";
    if (extra > 0) {
        const byType = (t) => list.filter((n) => n.dataType === t)
            .map((n) => "  " + n.sampleName + (n.passage ? ` (P${n.passage})` : ""));
        const wes = byType("WES"), rna = byType("RNA");
        tip = [
            wes.length ? `WES ${wes.length}건\n` + wes.join("\n") : "",
            rna.length ? `RNA ${rna.length}건\n` + rna.join("\n") : "",
        ].filter(Boolean).join("\n\n");
    }

    return (
        <div className={styles.ngsCell}>
            <div className={styles.ngsBadges}>
                <span className={r.hasWES ? `${styles.ngsBadge} ${styles.ngsWes}` : `${styles.ngsBadge} ${styles.ngsOff}`}>WES</span>
                <span className={r.hasRNA ? `${styles.ngsBadge} ${styles.ngsRna}` : `${styles.ngsBadge} ${styles.ngsOff}`}>RNA</span>
            </div>
            <span className={styles.ngsCode} title={tip || undefined}>
                {primary}
                {extra > 0 && <span className={styles.ngsMore}>+{extra}</span>}
            </span>
        </div>
    );
}
export function IC50Cell({ value, hasDrug }) {
    if (!hasDrug) return <span className={styles.ic50None} title="IC50">—</span>;
    if (value == null) return <span className={styles.ic50None}>n/a</span>;
    // value 는 "45.0 nM" / "> 10,000 nM" / "< 3.2 nM" / "N/A" 형태 문자열
    const str = String(value);
    const num = parseFloat(str.replace(/[^0-9.]/g, ""));   // 색상 판정용 수치 추출(nM)
    let cls = styles.ic50Ok;
    if (str.includes("<") || (!isNaN(num) && num < 100)) cls = styles.ic50Good;
    else if (str.includes(">") || (!isNaN(num) && num > 5000)) cls = styles.ic50Bad;
    else if (!isNaN(num) && num < 1000) cls = styles.ic50Mid;
    return <span className={cls} title={str}>{str}</span>;
}
export function FusionPill({ value, hasRNA }) {
    if (!hasRNA) return <span className={styles.ic50None}>—</span>;
    return <span className={value > 40 ? `${styles.fusionPill} ${styles.fusionHot}` : styles.fusionPill}>{value}</span>;
}